import {
  Component,
  OnDestroy,
  OnInit,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Observable, of, Subject } from "rxjs";
import { catchError, delay, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { MenuService } from "src/app/core/services/menu.service";
import { HrService } from "src/app/pages/hr/services/hr.service";
import { HR_HIDDEN_MENU_KEYS, HR_MENU_LINK, HR_TABS } from "src/app/pages/hr/hr-tabs";

import { MenuItem } from "../menu.model";
import { AuthService } from "src/app/core/services/auth.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
})
export class SidebarComponent implements OnInit, OnDestroy {
  menu: any;
  toggle: any = true;
  menuItems: MenuItem[] = [];
  @ViewChild("sideMenu") sideMenu!: ElementRef;
  @Output() mobileMenuButtonClicked = new EventEmitter();
  entityLogo: any;
  pathLogoImage = `${environment.apiUrl}/${environment.dirLogoImage}/`;

  isBlackSideBar = false;

  // Entidade do utilizador autenticado (vem com o logo).
  entityId: number | null = null;

  // Substituições provisórias de chave de menu por entidade (a chave nova também
  // se traduz). A 56 quer ver "Parcerias/Academia" até existir o menu Parcerias.
  private readonly menuLabelOverrides: Record<number, Record<string, string>> =
    {
      56: { "MENUITEMS.ACADEMY.TEXT": "MENUITEMS.PARTNERSHIPS_ACADEMY.TEXT" },
    };

  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    public translate: TranslateService,
    private menuService: MenuService,
    private authService: AuthService,
    private hrService: HrService,
  ) {}

  ngOnInit(): void {
    // Já conhecida no arranque; o pedido do logo confirma-a a seguir.
    this.entityId = this.authService.currentUserValue?.entity ?? null;

    // Menu Items
    this.menuService
      .getMenus()
      .pipe(
        switchMap((items) => this.withHrSubmenu(items)),
        tap((data) => (this.menuItems = data)),
        delay(1),
      )
      .subscribe((data) => {
        this.initActiveMenu();
      });

    // Os números das páginas de RH (pedidos por decidir...) em badge no submenu.
    this.hrService.tabCounts$
      .pipe(takeUntil(this.destroy$))
      .subscribe((counts) => this.applyHrCounts(counts as Record<string, number>));

    this.authService.getLogo().subscribe((data) => {
      this.entityId = data?.entityId ?? null;
      if (data.logo) {
        this.entityLogo = this.pathLogoImage + data.logo;
        this.isBlackSideBar = [56, 67].includes(data.entityId);
      } else {
        this.entityLogo = "assets/images/your-logo2.png";
        this.isBlackSideBar = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Recursos Humanos: o item `/hr` ganha um submenu com as páginas que a
   * pessoa pode ver (vem de `/hr/meta`, a mesma regra das páginas) e passa a
   * chamar-se "Recursos Humanos", mesmo que a linha `menus` ainda diga
   * "Recrutamento". Sem meta, fica como um link simples.
   */
  private withHrSubmenu(items: MenuItem[]): Observable<MenuItem[]> {
    const hr = items.find((item) => item.link === HR_MENU_LINK && !item.isTitle);
    if (!hr) return of(items);
    hr.label = "MENUITEMS.HR.TEXT";
    return this.hrService.getMeta().pipe(
      map((meta) => {
        const visible = HR_TABS.filter((tab) => (meta?.tabs as any)?.[tab.key]);
        if (visible.length > 1) {
          hr.subItems = visible.map((tab) => ({
            id: `hr-${tab.key}`,
            parentId: hr.id,
            label: tab.label,
            link: `${HR_MENU_LINK}/${tab.route}`,
            hrKey: tab.key,
            // Escondido só no menu (display none): a página continua acessível.
            hidden: HR_HIDDEN_MENU_KEYS.includes(tab.key),
          }));
          hr.navigate = true;
          this.hrService.publishCounts(meta?.counts);
        }
        return items;
      }),
      catchError(() => of(items)),
    );
  }

  private applyHrCounts(counts: Record<string, number>): void {
    const hr = this.menuItems.find((item) => item.link === HR_MENU_LINK);
    (hr?.subItems || []).forEach((subitem: any) => {
      const count = counts?.[subitem.hrKey] ?? 0;
      subitem.badge = count > 0 ? { variant: "bg-danger", text: `${count}` } : undefined;
    });
  }

  /**
   * Item com submenu. Os normais só abrem e fecham; o de RH também leva à
   * visão geral e deixa o submenu aberto. Já dentro de RH, fecha-o.
   * Os pais da tabela `menus` com link (`navigateFromChildren`, ex.: Vendas)
   * só contam como "dentro" na própria página: a partir de um filho (previsão)
   * o clique volta à página e deixa o submenu aberto.
   */
  onParentClick(event: any, item: MenuItem): void {
    if (!item.navigate || !item.link) {
      this.toggleItem(event);
      return;
    }
    // O href é o link real (para o item ficar ativo ao recarregar); a navegação é do router.
    event.preventDefault?.();
    const anchor = event.target.closest("a.nav-link");
    const isOpen = !!anchor?.nextElementSibling?.classList.contains("show");
    const currentUrl = this.router.url.split("?")[0];
    const insideSection = item.navigateFromChildren
      ? currentUrl === item.link
      : this.router.url.startsWith(item.link);
    if (!isOpen || insideSection) this.toggleItem(event);
    if (!insideSection) this.router.navigate([item.link]);
  }

  removeActivation(items: any) {
    items.forEach((item: any) => {
      if (item.classList.contains("menu-link")) {
        if (!item.classList.contains("active")) {
          item.setAttribute("aria-expanded", false);
        }
        item.nextElementSibling
          ? item.nextElementSibling.classList.remove("show")
          : null;
      }
      if (item.classList.contains("nav-link")) {
        if (item.nextElementSibling) {
          item.nextElementSibling.classList.remove("show");
        }
        item.setAttribute("aria-expanded", false);
      }
      item.classList.remove("active");
    });
  }

  toggleSubItem(event: any) {
    let isCurrentMenuId = event.target.closest("a.nav-link");
    let isMenu = isCurrentMenuId.nextElementSibling as any;
    let dropDowns = Array.from(document.querySelectorAll(".sub-menu"));
    dropDowns.forEach((node: any) => {
      node.classList.remove("show");
    });

    let subDropDowns = Array.from(
      document.querySelectorAll(".menu-dropdown .nav-link"),
    );
    subDropDowns.forEach((submenu: any) => {
      submenu.setAttribute("aria-expanded", "false");
    });

    if (event.target && event.target.nextElementSibling) {
      isCurrentMenuId.setAttribute("aria-expanded", "true");
      event.target.nextElementSibling.classList.toggle("show");
    }
  }

  toggleExtraSubItem(event: any) {
    let isCurrentMenuId = event.target.closest("a.nav-link");
    let isMenu = isCurrentMenuId.nextElementSibling as any;
    let dropDowns = Array.from(document.querySelectorAll(".extra-sub-menu"));
    dropDowns.forEach((node: any) => {
      node.classList.remove("show");
    });

    let subDropDowns = Array.from(
      document.querySelectorAll(".menu-dropdown .nav-link"),
    );
    subDropDowns.forEach((submenu: any) => {
      submenu.setAttribute("aria-expanded", "false");
    });

    if (event.target && event.target.nextElementSibling) {
      isCurrentMenuId.setAttribute("aria-expanded", "true");
      event.target.nextElementSibling.classList.toggle("show");
    }
  }

  toggleItem(event: any) {
    let isCurrentMenuId = event.target.closest("a.nav-link");
    let isMenu = isCurrentMenuId.nextElementSibling as any;
    if (isMenu.classList.contains("show")) {
      isMenu.classList.remove("show");
      isCurrentMenuId.setAttribute("aria-expanded", "false");
    } else {
      let dropDowns = Array.from(
        document.querySelectorAll("#navbar-nav .show"),
      );
      dropDowns.forEach((node: any) => {
        node.classList.remove("show");
      });
      isMenu ? isMenu.classList.add("show") : null;
      // const ul = document.getElementById("navbar-nav");
      // if (ul) {
      //   const iconItems = Array.from(ul.getElementsByTagName("a"));
      //   let activeIconItems = iconItems.filter((x: any) => x.classList.contains("active"));
      //   activeIconItems.forEach((item: any) => {
      //     item.setAttribute('aria-expanded', "false")
      //     item.classList.remove("active");
      //   });
      // }
      // isCurrentMenuId.setAttribute("aria-expanded", "true");
      // if (isCurrentMenuId) {
      //   this.activateParentDropdown(isCurrentMenuId);
      // }
    }
  }

  activateParentDropdown(item: any) {
    item.classList.add("active");
    let parentCollapseDiv = item.closest(".collapse.menu-dropdown");

    if (parentCollapseDiv) {
      // to set aria expand true remaining
      parentCollapseDiv.classList.add("show");
      parentCollapseDiv.parentElement.children[0].classList.add("active");
      parentCollapseDiv.parentElement.children[0].setAttribute(
        "aria-expanded",
        "true",
      );
      if (parentCollapseDiv.parentElement.closest(".collapse.menu-dropdown")) {
        parentCollapseDiv.parentElement
          .closest(".collapse")
          .classList.add("show");
        if (
          parentCollapseDiv.parentElement.closest(".collapse")
            .previousElementSibling
        )
          parentCollapseDiv.parentElement
            .closest(".collapse")
            .previousElementSibling.classList.add("active");
        if (
          parentCollapseDiv.parentElement
            .closest(".collapse")
            .previousElementSibling.closest(".collapse")
        ) {
          parentCollapseDiv.parentElement
            .closest(".collapse")
            .previousElementSibling.closest(".collapse")
            .classList.add("show");
          parentCollapseDiv.parentElement
            .closest(".collapse")
            .previousElementSibling.closest(".collapse")
            .previousElementSibling.classList.add("active");
        }
      }
      return false;
    }
    return false;
  }

  reloadCurrentRoute() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate([this.router.url]);
  }

  updateActive(event: any) {
    const ul = document.getElementById("navbar-nav");
    if (ul) {
      const items = Array.from(ul.querySelectorAll("a.nav-link"));
      this.removeActivation(items);
    }
    this.activateParentDropdown(event.target);
    this.SidebarHide();
  }

  // removeActiveWithoutDropdown() {

  //   const ul = document.getElementById("navbar-nav");
  //   if (ul) {
  //     const items = Array.from(ul.querySelectorAll("a.nav-link"));
  //     this.removeActivation(items);
  //   }
  // }

  removeActiveWithoutDropdown(event: any) {
    const el =
      event?.target?.tagName === "A"
        ? event.target
        : event.target.parentElement;
    if (!el?.classList?.contains("active")) {
      const ul = document.getElementById("navbar-nav");
      if (ul) {
        const items = Array.from(ul.querySelectorAll("a.nav-link"));
        this.removeActivation(items);
      }
    }
    this.SidebarHide();
  }

  initActiveMenu() {
    const pathName = window.location.pathname;
    const ul = document.getElementById("navbar-nav");

    if (ul) {
      const items = Array.from(ul.querySelectorAll("a.nav-link"));
      let activeItems = items.filter((x: any) =>
        x.classList.contains("active"),
      );
      this.removeActivation(activeItems);

      let matchingMenuItem = items.find((x: any) => {
        return x.pathname === pathName;
      });
      if (matchingMenuItem) {
        this.activateParentDropdown(matchingMenuItem);
        // Pai com página própria (ex.: Vendas): ao recarregar na sua página
        // fica ativo e com o submenu aberto, como depois de um clique.
        if (matchingMenuItem.classList.contains("is-parent")) {
          matchingMenuItem.nextElementSibling?.classList.add("show");
          matchingMenuItem.setAttribute("aria-expanded", "true");
        }
      }
    }
  }

  /** Nome a mostrar: a substituição da entidade, se existir, senão a chave normal. */
  menuLabel(item: MenuItem): string {
    const override =
      this.entityId != null
        ? this.menuLabelOverrides[this.entityId]?.[item.label]
        : undefined;
    return override ?? item.label;
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }

  /**
   * Toggle the menu bar when having mobile screen
   */
  toggleMobileMenu(event: any) {
    var sidebarsize =
      document.documentElement.getAttribute("data-sidebar-size");
    if (sidebarsize == "sm-hover-active") {
      document.documentElement.setAttribute("data-sidebar-size", "sm-hover");
    } else {
      document.documentElement.setAttribute(
        "data-sidebar-size",
        "sm-hover-active",
      );
    }
  }

  /**
   * SidebarHide modal
   * @param content modal content
   */
  SidebarHide() {
    document.body.classList.remove("vertical-sidebar-enable");
  }
}
